import { computed, onMounted, ref } from 'vue'

// Port of the Laravel Vue starter kit's useAppearance composable. Dark mode only
// ever applies to the back office (V2) so the customer-facing side stays light.

const BACKOFFICE_CLASS = 'backoffice'

export function isBackofficeComponent(component) {
  return typeof component === 'string' && component.startsWith('V2/')
}

const prefersDark = () => {
  if (typeof window === 'undefined') {
    return false
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

const getStoredAppearance = () => {
  if (typeof window === 'undefined') {
    return null
  }

  return localStorage.getItem('appearance')
}

const setCookie = (name, value, days = 365) => {
  if (typeof document === 'undefined') {
    return
  }

  const maxAge = days * 24 * 60 * 60

  document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`
}

export function updateTheme(value) {
  if (typeof window === 'undefined') {
    return
  }

  const root = document.documentElement
  const dark = root.classList.contains(BACKOFFICE_CLASS) && (value === 'system' ? prefersDark() : value === 'dark')

  root.classList.toggle('dark', dark)
  root.style.colorScheme = dark ? 'dark' : 'light'
}

export function syncBackofficeTheme(component, backofficeUi) {
  if (typeof window === 'undefined') {
    return
  }

  document.documentElement.classList.toggle(BACKOFFICE_CLASS, isBackofficeComponent(component) && backofficeUi === 'v2')
  updateTheme(getStoredAppearance() || 'system')
}

export function initializeTheme(component, backofficeUi) {
  if (typeof window === 'undefined') {
    return
  }

  syncBackofficeTheme(component, backofficeUi)

  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => updateTheme(getStoredAppearance() || 'system'))
}

const appearance = ref('system')

export function useAppearance() {
  onMounted(() => {
    const savedAppearance = getStoredAppearance()

    if (savedAppearance) {
      appearance.value = savedAppearance
    }
  })

  const resolvedAppearance = computed(() => {
    if (appearance.value === 'system') {
      return prefersDark() ? 'dark' : 'light'
    }

    return appearance.value
  })

  function updateAppearance(value) {
    appearance.value = value

    // Store in localStorage for client-side persistence...
    localStorage.setItem('appearance', value)

    // Store in cookie for SSR...
    setCookie('appearance', value)

    updateTheme(value)
  }

  return {
    appearance,
    resolvedAppearance,
    updateAppearance,
  }
}
